<template>
	<div v-if="availableLogistics.length > 0" class="notranslate">
		<div v-if="!cartLogistic" class="alert alert-warning">{{$t('please_select_logistic')}}</div>
		<div class="form-group row">
			<div class="col-12">
				<label class="form-control-label">{{$t('logistic')}}</label>
				<ding-select :options="availableLogistics" v-model="selectedLogistic" valueAttribute="id" labelAttribute="display_name" :preselect-first="false"></ding-select>
			</div>
		</div>
		<div v-if="selectedLogistic?.free_shipping_for_order_amount || selectedLogistic?.free_shipping_for_count" class="alert alert-success">
			<div v-if="selectedLogistic.free_shipping_for_order_amount">{{ $t('logistics.free_shipping_for_order_amount', {amount: getExchangedAmount(selectedLogistic.free_shipping_for_order_amount)}) }}</div>
			<div v-if="selectedLogistic.free_shipping_for_count">{{ $t('logistics.free_shipping_for_count', {count: selectedLogistic.free_shipping_for_count}) }}</div>
		</div>
		<div v-if="!cartEligibleForFreeShipping && (appliedLogisticRule && appliedLogisticRule.not_handled)" class="form-group row">
			<div class="col-12 pl-3">
				<div class="alert alert-warning">
					<div v-if="cartLogistic.rule_type === 'BY_COUNT'">
						{{$t('logistic_rule_not_handled_automatically_please_contact_us_count', appliedLogisticRule)}}
					</div>
					<div v-else-if="cartLogistic.rule_type === 'BY_AMOUNT'">
						{{$t('logistic_rule_not_handled_automatically_please_contact_us_amount', { min: getExchangedAmount(appliedLogisticRule.min) })}}
					</div>
				</div>
			</div>
		</div>
		<div v-else-if="addressRequired" class="form-group row">
			<div v-if="!hasValidInformation && showInvalid"
				class="col-12 pl-3">
				<div v-if="!cartLogistic" class="alert alert-warning">{{$t('please_select_logistic')}}</div>
				
				<div v-else-if="this.addressRequired && !this.isAddressValid" class="alert alert-warning">{{$t('please_provider_address')}}</div>
				<div v-if="!hasValidName" class="alert alert-warning">
					{{$t('message.please_fill_your_name')}}
				</div>
				<div v-if="!hasValidPhoneNumber" class="alert alert-warning">
					{{$t('message.please_fill_your_phone')}}
				</div>
			</div>
			<div class="col-12">
				<label class="form-control-label">{{$t('delivery_address')}}</label>
				<Address :originalAddress="cartAddress" @input="setCartAddress" :region-limitation="cartLogistic ? cartLogistic.area : null"/>
			</div>
			<div class="col-12 pt-3">
				<label class="form-control-label">{{$t('receiver')}}</label>
				<div class="form-group row">
					<label class="form-control-label col-auto text-right mb-0 d-flex align-items-center">{{$t('receiver_name_label')}}</label>
					<div class="col pl-0">
						<input class="form-control ding-input" type="text" :value="receiverName" @input="onReceiverNameInput" :placeholder="$t('receiver_name_placeholder')" />
					</div>
				</div>
				<div class="form-group row">
					<label class="form-control-label col-auto text-right mb-0 d-flex align-items-center">{{$t('receiver_phone_label')}}</label>
					<vue-phone-input
						class="col pl-0"
						ref="phoneInput"
						name="phone"
						:code="countryCode"
						:value="phoneNumber"
						:onChange="onReceiverPhoneInput"
						:placeholder="$t('receiver_phone_placeholder')"
					/>
				</div>
			</div>
		</div>
		<div v-if="logisticRuleRequired && appliedLogisticRule && appliedLogisticRule.not_handled" class="text-right">
		</div>
		<div v-else-if="!logisticRuleRequired || (logisticRuleRequired && appliedLogisticRule)" class="text-right">
			<hr>
			<div>{{$t('logistic_subtotal', { total: getExchangedAmount(logisticTotal)})}}</div>
		</div>
		<div v-else-if="logisticRuleRequired && (!appliedLogisticRule && !cartEligibleForFreeShipping)" class="text-danger text-right">
			<hr>
			<div>{{$t('cant_determine_logistic_rule')}}</div>
		</div>
	</div>
	<div v-else-if="logistics.length > 0" class="text-center">
		<div class="text-warning " v-html="$t('no_common_logistic')"></div>
		<div v-for="cartItem in cartItems">
			<div class="">
				<label class="d-inline">{{itemOrSpecForCartItem(cartItem).name}}</label>
				<small cass="text-muted d-inline-block">
					{{ itemOrSpecForCartItem(cartItem).logisticIds.map(logisticId => logisticMap[logisticId].display_name).join(" / ")}}
				</small>
			</div>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import RootStore from '../stores/root';
import DingSelect from '../components/common/Select';
import Address from './common/Address';
import VuePhoneInput from '../components/vue-phone-input/index.vue';
import { getExchangedAmount } from '../bu/CurrencyUtils';
import { isValidPhoneNumber } from 'libphonenumber-js';

export default {
	components: { DingSelect, Address, VuePhoneInput },
	store: RootStore,
	props: {
		context: { required: true, type: Object },
		provider: { required: true, type: Object },
	},
	computed: {
		...mapGetters({
			receiverName: 'cart/receiverName',
			receiverPhone: 'cart/receiverPhone',
			items: 'items/items',
			itemTotal: 'cart/itemTotal',
			allCartItems: 'cart/items',
			totalItemCount: 'cart/totalItemCount',
			totalItemCountForLogistic: 'cart/totalItemCountForLogistic',
			logistics: 'logistics/allLogistics',
			logisticMap: 'logistics/logisticMap',
			cartLogistic: 'cart/logistic',
			cartAddress: 'cart/address',
			cartEligibleForFreeShipping: 'cart/eligibleForFreeShipping',
			currency: 'currencies/currency',
            currencyRateMap: 'currencies/currencyRateMap',
			currencyOptions: 'currencies/currencyOptions',
		}),
		hasValidInformation() {
			if (!this.cartLogistic) {
				return false;
			}
			if (this.addressRequired && !this.isAddressValid) {
				return false;
			}
			if (!this.cartEligibleForFreeShipping && (this.logisticRuleRequired && !this.appliedLogisticRule)) {
				return false;
			}
			if (!this.cartEligibleForFreeShipping && (this.logisticRuleRequired && this.appliedLogisticRule && this.appliedLogisticRule.not_handled)) {
				return false;
			}
			if (!this.hasValidName) {
				return false;
			}
			if (!this.hasValidPhoneNumber) {
				return false;
			}
			return true;
		},
		logisticRuleRequired() {
			return this.selectedLogistic && this.selectedLogistic.type !== 'FREE_OR_SELF_TAKEN';
		},
		isAddressValid() {
			return !!(this.cartAddress.country
				&& this.cartAddress.postal_code
				&& this.cartAddress.admin_level_1
				&& this.cartAddress.admin_level_2
				&& this.cartAddress.address);
		},
		addressRequired() {
			return this.selectedLogistic && this.selectedLogistic.type !== 'FREE_OR_SELF_TAKEN';
		},
		cartItems() {
			return this.allCartItems.filter((item) => (item.itemId || item.specId) && item.count > 0);
		},
		availableLogistics() {
			return this.logistics.filter((l) => {
				for(var i = 0 ; i < this.cartItems.length ; i++) {
					const item = this.itemOrSpecForCartItem(this.cartItems[i]);
					if (item.logisticIds.length && !item.logisticIds.find((id) => id === l.id)) {
						return false;
					}
				}
				return true;
			}).sort((a, b) => a.order - b.order);
		},
		hasCommonLogistic() {
			return this.availableLogistics.length > 0;
		},
		appliedLogisticRule() {
			const l = this.selectedLogistic;
			if (!l) {
				return null;
			}
			if (l.type === 'FREE_OR_SELF_TAKEN') {
				return null;
			} else if (l.rule_type === 'FIXED') {
				return l.rules[0];
			} else if (l.rule_type === 'BY_COUNT') {
				for(var i = 0 ; i < l.rules.length ; i++) {
					let rule = l.rules[i];
					if (rule.min <= this.totalItemCountForLogistic && rule.max >= this.totalItemCountForLogistic) {
						return rule;
					}
					if (rule.min && !rule.max && this.totalItemCountForLogistic >= rule.min) {
						return rule;
					} 
				}
			} else if (l.rule_type === 'BY_AMOUNT') {
				for(var i = 0 ; i < l.rules.length ; i++) {
					let rule = l.rules[i];
					if (rule.min <= this.itemTotal && rule.max >= this.itemTotal) {
						return rule;
					}
					if (rule.min && !rule.max && this.itemTotal >= rule.min) {
						return rule;
					} 
				}
			}
		},
		logisticTotal() {
			const l = this.selectedLogistic;
			if (!l || l.type === 'FREE_OR_SELF_TAKEN' || !this.appliedLogisticRule) {
				return 0;
			}
			if (this.cartEligibleForFreeShipping) {
				return 0;
			}
			if (this.appliedLogisticRule && this.appliedLogisticRule.not_handled) {
				return 0;
			}
			return this.appliedLogisticRule.price;
		},
		phoneNumber() {
			return this.receiverPhone && ('0' + this.receiverPhone.split('886-')[1]);
		},
		hasValidName() {
            return !this.receiverName || this.receiverName.length >= 2;
        },
        hasValidPhoneNumber() {
            return !this.receiverPhone || (!!this.countryCode && isValidPhoneNumber(this.receiverPhone, this.countryCode?.toUpperCase()));
        },
	},
	data() {
		return {
			selectedLogistic: null,
			showInvalid: false,
			countryCode: 'tw'
		}
	},
	async mounted() {
		await this.getLogistics();
		this.selectedLogistic = this.cartLogistic;
	},
	destroyed() {
		this.resetCartLogistic();
	},
	watch: {
		selectedLogistic() {
			this.setCartLogistic(this.selectedLogistic);
		},
		appliedLogisticRule() {
			this.setCartLogisticRule(this.appliedLogisticRule);
		},
		logisticTotal() {
			this.setCartLogisticTotal(this.logisticTotal);
		},
		hasCommonLogistic() {
			if(!this.hasCommonLogistic) {
				this.selectedLogistic = null;
			}
		},
		availableLogistics() {
			if (this.selectedLogistic && !this.availableLogistics.find((l) => l.id === this.selectedLogistic.id)) {
				this.selectedLogistic = null;
			}
		}
	},
	methods: {
		...mapActions({
			getLogistics: 'logistics/getLogistics',
			setCartLogistic: 'cart/setLogistic',
			setCartLogisticRule: 'cart/setLogisticRule',
			setCartAddress: 'cart/setAddress',
			setReceiverName: 'cart/setReceiverName',
			setReceiverPhone: 'cart/setReceiverPhone',
			setCartLogisticTotal: 'cart/setCartLogisticTotal',
			resetCartLogistic: 'cart/resetCartLogistic',
		}),
		onReceiverNameInput(e) {
			this.setReceiverName(e.target.value);
		},
		onReceiverPhoneInput(phoneInputComponent) {
			const phoneNumber = +phoneInputComponent.phoneNumber;
			const phoneCallingCode = phoneInputComponent.availableData[phoneInputComponent.countryCode].dialCode;
			const phone = `+${phoneCallingCode}-${phoneNumber}`;
			this.setReceiverPhone(phone);
			this.countryCode = phoneInputComponent.countryCode;
		},
		highlightInvalidState() {
			this.showInvalid = true;
		},
		itemOrSpecForCartItem(cartItem) {
			if (cartItem.specId) {
				return this.specForCartItem(cartItem);
			} else {
				return this.itemForCartItem(cartItem);
			}
		},
		itemForCartItem(cartItem) {
			return this.items.find((item) => item.id == cartItem.itemId)
		},
		specForCartItem(cartItem) {
			if (!cartItem.specId) {
				return null;
			}
			return this.itemForCartItem(cartItem).specs.find((spec) => spec.id == cartItem.specId)
		},
		getExchangedAmount(amount) {
            return getExchangedAmount({
                rateMap: this.currencyRateMap,
                amount,
                from: this.provider.currency || 'TWD',
                to: this.currency,
				currencyOptions: this.currencyOptions,
            });
        },
	}
}
</script>

<style lang="scss" scoped>

.ding-input {
	border: none;
	padding: 0.375rem 1.2rem !important;
	background: #F2F7F8;
	height: auto !important;
}

/deep/ .intl-phone-input .selected-flag {
	// display: none;
}

/deep/ .intl-phone-input.allow-dropdown input[type="text"] {
	border: none;
	background-color: #F2F7F8;
	padding-top: 1.2rem;
	padding-bottom: 1.2rem;
	font-size: inherit;
}

/deep/ .intl-phone-input.allow-dropdown input[type="text"]:focus {
	background-color: #fff;
	border-color: #ffe681;
	box-shadow: 0 0 0 0.2rem rgba(255, 204, 1, 0.25);
}

</style>

<i18n>
{
	"zh": {
		"logistic": "配送方式",
		"delivery_address": "寄送地址",
		"no_common_logistic": "沒有共通的配送方式可配送購物袋內所有商品。<br>請參考下列商品可選配送方式，調整購物內容。",
		"logistic_subtotal": "物流費用小計 {total}",
		"please_select_logistic": "請選擇配送方式",
		"please_provider_address": "請選擇配送地址",
		"cant_determine_logistic_rule": "無法計算物流金額，請聯絡我們",
		"logistic_rule_not_handled_automatically_please_contact_us": "您選購的內容。",
		"logistic_rule_not_handled_automatically_please_contact_us_count": "購買數量超過物流可接受上限，請修改購買數量。若您需購買 {min} 個以上，請直接聯繫我們。",
		"logistic_rule_not_handled_automatically_please_contact_us_amount": "購買金額超過物流設定上限，請修改購買數量。若您購買金額超過 {min} 以上，請直接聯繫我們。",
		"receiver": "收件人",
		"receiver_name_label": "姓名",
		"receiver_name_placeholder": "(可不填，預設為預約者姓名)",
		"receiver_phone_label": "手機",
		"receiver_phone_placeholder": "(可不填，預設為預約者號碼)"
	},
	"en": {
		"logistic": "Delivery Method",
		"delivery_address": "Address",
		"logistic_subtotal": "Shipping costs subtotal {total}",
		"no_common_logistic": "No common delivery method for all items",
		"please_select_logistic": "Please select delivery method",
		"please_provider_address": "Please provide address",
		"cant_determine_logistic_rule": "Fail to determine delivery rules, please contact us.",
		"logistic_rule_not_handled_automatically_please_contact_us": "Your cart content can't be purchased through this delivery method. Please contact us directly. Thanks.",
		"logistic_rule_not_handled_automatically_please_contact_us_count": "This delivery method can't support buying over {min} items. If you need to buy items more than this, please contact us.",
		"logistic_rule_not_handled_automatically_please_contact_us_amount": "This delivery method can't support buying over {min}. If you need to buy items with total amount more than this, please contact us.",
		"receiver": "Receiver",
		"receiver_name_label": "Name",
		"receiver_name_placeholder": "(Default is Your name)",
		"receiver_phone_label": "Phone",
		"receiver_phone_placeholder": "(Default is Your phone number)"
	}
}
</i18n>